import { useEffect, useReducer } from 'react';
import { createJoki } from 'joki';

import createFetchService from './services/FetchService/FetchService';
import createSocketIoService from './services/SocketIoService';
import AuthenticationService from './services/Authentication/AuthenticationService';
import createDevicesService from './services/DeviceService/DevicesService';
import createDeviceDetailService from './services/DeviceService/SelectedDeviceService';
import createUserService from './services/User/UserService';
import createAlertsService from './services/Alerts/AlertsService';
import createNotificationService from './services/Notifications/NotificationService';
import createGlobalStoreService from './services/GlobalStoreService/GlobalStoreService';
import createGraphQLService from './services/GraphQlService/GraphQLService';
import createEndpointService from './services/EndpointService/EndpointService';
import createOrganizationService from './services/Organizations/OrganizationService';
import createLocationService from './services/Locations/LocationService';

/**Alaram notification settins */
import createAlertSettingsService from './services/AlertSettings/AlertSettingsService';

// Endpoint settings
import { endpoints, endpointServices } from './services/endpoints';
import createConfigService from './services/Config/ConfigService';

// console.log("JOKI IDENTIFIER", identifier);

const JOKI = createJoki({ debug: false });

function initializeServices(jokiInstance) {
    new AuthenticationService(jokiInstance);
    createFetchService(jokiInstance);
    createConfigService(jokiInstance, {
        defaults: {
            mainAdminRole: [{ role: 'Administrator', accessScope: 'Root' }],

            organizationCRUDRoles: [
                { role: 'Administrator', accessScope: 'Root' },
                { role: 'Partner Admin', accessScope: 'Root' },
                { role: 'Delivery', accessScope: 'Root' },
            ],

            locationCRUDRoles: [
                { role: 'Administrator', accessScope: 'Root' },
                { role: 'Partner Admin', accessScope: 'Root' },
                { role: 'Delivery', accessScope: 'Root' }
            ],

            deviceManagementRoles: [
                { role: 'Administrator', accessScope: '' },
                { role: 'Partner Admin', accessScope: '' },
                { role: 'Delivery', accessScope: '' },
                { role: 'Cabinet Operator', accessScope: '' },
                { role: 'Industrial Operator', accessScope: '' },
            ],

            AlertNotificationSettingsRoles:[
                {role: 'Administrator', accessScope: ''}
            ]            
        },
    });
    createSocketIoService(jokiInstance);
    createUserService(jokiInstance);
    createGlobalStoreService(jokiInstance);
    createDevicesService(jokiInstance);
    createDeviceDetailService(jokiInstance);
    createAlertsService(jokiInstance);
    createNotificationService(jokiInstance);
    createGraphQLService(jokiInstance, {
        url: process.env.REACT_APP_API_URL,
        serviceId: 'GraphQLService',
    });
    createEndpointService(jokiInstance, { services: endpointServices }, endpoints);
    createOrganizationService(jokiInstance);
    createLocationService(jokiInstance);

    /**Alaram notification settins */
    createAlertSettingsService(jokiInstance);
}

// Make some basic wrappers for this Joki instance, so that we do not have to include JOKI instance everytime on components.

/**
 * Send message to Joki
 * @param {*} opts
 */
function send(event) {
    JOKI.trigger(event);
}

function useServiceReducer(state, action) {
    switch (action.type) {
        case 'update':
            return {
                version: state.version + 1,
                data: action.data || state.data,
                previous: state.data,
                reset: false,
            };
        case 'initial':
            return {
                version: state.data === null || action.data === null ? null : 0,
                data: action.data,
                previous: null,
                reset: false,
            };
        case 'reset':
            return {
                version: null,
                data: action.data || null,
                previous: null,
                reset: true,
            };
        default:
            return state;
    }
}

function dataSetIsEmpty(data) {
    if (Array.isArray(data) || data instanceof WeakMap || data instanceof WeakSet) {
        return data.length === 0;
    }

    if (data === undefined) {
        return true;
    }

    if (data === null) {
        return true;
    }

    if (data instanceof Map || data instanceof Set) {
        return data.size === 0;
    }

    if (typeof data === 'object') {
        return Object.keys(data).length === 0;
    }

    return false;
}

/**
 * Get the current state of a registered service in Joki
 * @param {*} serviceId
 */
function useService(serviceId, async = true, initBodyOpts = false) {
    // Prefetch data if necessary
    let preData = null;

    // let preInit = false;
    if (async === false) {
        const data = askJoki({ to: serviceId, key: 'getState', syncAsk: true, body: initBodyOpts });
        preData = data[serviceId];
        // preInit = true;
    }

    // const [initialized, setInitialized] = useState(preInit);

    const [state, dispatch] = useReducer(useServiceReducer, { data: preData, version: null, previous: null, reset: false });

    // const [data, setData] = useState(preData);
    // const [version, setVersion] = useState(0);

    useEffect(() => {
        const initBodyData = initBodyOpts !== false ? initBodyOpts : {};
        askJoki({
            to: serviceId,
            key: 'getState',
            body: initBodyData,
        }).then(data => {
            // setData(data[serviceId]);
            if (!dataSetIsEmpty(data[serviceId])) {
                dispatch({
                    type: 'initial',
                    data: data[serviceId],
                });
            }
        });
    }, [serviceId, initBodyOpts]);

    useEffect(() => {
        return JOKI.on({
            from: 'EndpointService',
            key: 'DataResetRequired',
            fn: () => {
                dispatch({
                    type: 'reset',
                    data: preData,
                });
            },
        });
    }, [preData]);
    useEffect(() => {
        return JOKI.on({
            from: serviceId,
            key: 'serviceReset',
            fn: event => {
                const dataValue = event.body ? event.body : preData ? preData : null;
                dispatch({
                    type: 'reset',
                    data: dataValue,
                });
            },
        });
    }, [preData, serviceId]);

    useEffect(() => {
        return JOKI.on({
            from: serviceId,
            fn: event => {
                if (event.from === serviceId && event.serviceUpdate === true && event.key !== 'serviceUpdate') {
                    dispatch({ type: 'update', data: event.body });
                    // setData(event.body);
                    // setVersion(prev => { return prev + 1});
                }
            },
        });
    }, [serviceId]);

    useEffect(() => {
        return JOKI.on({
            from: serviceId,
            key: 'serviceUpdate',
            fn: event => {
                if (event.from === serviceId && event._serviceUpdate === true) {
                    dispatch({ type: 'update', data: event.body });
                    // setData(event.body);
                    // setVersion(prev => { return prev + 1});
                }
            },
        });
    }, [serviceId]);

    function triggerService(event) {
        event.to = serviceId;
        // event.from = event.from !== undefined ? event.from : `useService-${serviceId}`;
        send(event);
    }

    function askService(event) {
        event.syncAsk = event.syncAsk !== undefined ? event.syncAsk : false;
        // event.from = event.from !== undefined ? event.from : `useService-${serviceId}`;
        event.to = serviceId;
        return JOKI.ask(event);
    }

    return [state.data, triggerService, askService, state.version, state.previous];
}

/**
 * Listen for events in Joki
 *
 * NOT IN USE
 * @param {*} eventKey
 */
function useEvent(eventKey) {
    // const [data] = useListenJokiEvent(JOKI, eventKey);
    return null;
}

function askJoki(event) {
    event.syncAsk = event.syncAsk !== undefined ? event.syncAsk : false;
    return JOKI.ask(event);
}

export { JOKI, send, useService, useEvent, askJoki, initializeServices };
